import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { API_PATHS } from "../api/apiPaths";
import { http } from "../api/httpClient";

const FALLBACK_CATEGORIES = [
  "Interior Designers",
  "Architects",
  "Civil Contractors",
  "Electricians",
  "Carpenters",
  "Painters",
];

const toCategoryLabel = (category) =>
  String(category?.name || category?.label || category?.title || "").trim();

function RevampCategoryMarquee() {
  const [categories, setCategories] = useState(FALLBACK_CATEGORIES);
  const [repeatFactor, setRepeatFactor] = useState(1);
  const stripRef = useRef(null);
  const trackRef = useRef(null);

  useEffect(() => {
    const controller = new AbortController();

    const fetchCategories = async () => {
      try {
        const { data: responseData } = await http.get(API_PATHS.CATEGORIES, {
          signal: controller.signal,
        });
        const list = Array.isArray(responseData) ? responseData : responseData?.data || [];
        const labels = list.map(toCategoryLabel).filter(Boolean);
        if (labels.length > 0) setCategories(labels);
      } catch (error) {
        if (error?.name === "AbortError" || error?.code === "ERR_CANCELED") return;
        setCategories(FALLBACK_CATEGORIES);
      }
    };

    fetchCategories();
    return () => controller.abort();
  }, []);

  const loopItems = useMemo(() => {
    const base = categories.length ? categories : FALLBACK_CATEGORIES;
    const repeatedBase = Array.from({ length: repeatFactor }, () => base).flat();
    return [...repeatedBase, ...repeatedBase];
  }, [categories, repeatFactor]);

  useLayoutEffect(() => {
    const stripEl = stripRef.current;
    const trackEl = trackRef.current;
    if (!stripEl || !trackEl) return;

    const base = categories.length ? categories : FALLBACK_CATEGORIES;
    if (base.length === 0) return;

    const recompute = () => {
      const viewportWidth = stripEl.clientWidth || 0;
      if (!viewportWidth) return;
      const halfWidth = trackEl.scrollWidth / 2;
      const targetHalfWidth = viewportWidth * 2;

      if (halfWidth > 0 && halfWidth < targetHalfWidth) {
        // Compute the next factor from current measured width only.
        // Avoid multiplying by previous factor which can cause cascading growth.
        const nextFactor = Math.min(12, Math.max(2, Math.ceil(targetHalfWidth / halfWidth)));
        setRepeatFactor((prev) => (prev === nextFactor ? prev : nextFactor));
      }
    };

    const raf = requestAnimationFrame(recompute);
    window.addEventListener("resize", recompute);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", recompute);
    };
  }, [categories]);

  return (
    <div className="revamp-category-strip-shell" aria-label="Popular categories">
      <div className="revamp-category-strip" ref={stripRef}>
        <div className="revamp-category-track" aria-hidden="true" ref={trackRef}>
          {loopItems.map((name, index) => (
            <div className="revamp-category-item" key={`${name}-${index}`}>
              <span className="revamp-category-dot" aria-hidden="true" />
              <span className="revamp-category-name">{name}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default RevampCategoryMarquee;

